import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang, IMAGES } from "../i18n";
import { SectionLabel } from "./Motion";
import { ArrowUpRight, Plus } from "lucide-react";

export const Services = () => {
  const { t } = useLang();
  const s = t.services;
  const [expanded, setExpanded] = useState(false);

  return (
    <section id="services" data-testid="services-section" className="relative py-20 md:py-32 overflow-hidden">
      <div className="absolute -top-20 left-1/3 w-[520px] h-[520px] rounded-full bg-[var(--green)]/10 blur-[160px] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex items-end justify-between gap-6 mb-4">
          <div className="max-w-3xl">
            <SectionLabel>{s.label}</SectionLabel>
            <h2 className="font-cormorant font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-[var(--text-primary)]">
              {s.title}
            </h2>
          </div>
          <button
            onClick={() => setExpanded((e) => !e)}
            data-testid="services-toggle"
            aria-expanded={expanded}
            className="group flex items-center gap-4 shrink-0 pb-2"
          >
            <span className="font-jetbrains text-[10px] tracking-[0.2em] uppercase text-[var(--text-secondary)] group-hover:text-[var(--gold)] transition-colors hidden sm:block">
              {expanded ? s.collapse : s.expand}
            </span>
            <span className="w-14 h-14 rounded-full border border-[var(--border)] flex items-center justify-center text-[var(--text-primary)] group-hover:border-[var(--gold)] group-hover:text-[var(--gold)] transition-colors duration-300">
              <motion.span animate={{ rotate: expanded ? 45 : 0 }} transition={{ duration: 0.3 }} className="flex">
                <Plus className="w-6 h-6" />
              </motion.span>
            </span>
          </button>
        </div>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="services-grid-wrap"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8" data-testid="services-grid">
          {s.items.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (i % 2) * 0.1 }}
              data-testid={`service-card-${i}`}
              className="group relative overflow-hidden rounded-[1.5rem] border border-[var(--border)] bg-[var(--bg-secondary)]"
            >
              <div className="relative h-44 overflow-hidden">
                <img
                  src={IMAGES[item.img]}
                  alt={item.t}
                  className="w-full h-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-secondary)] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 w-5 h-5 border-t border-l border-[var(--gold)]/50" />
                <div className="absolute top-4 right-4 glass rounded-full px-3 py-1 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--green)] animate-pulse" />
                  <span className="font-jetbrains text-[9px] tracking-widest text-[var(--text-primary)]">AI</span>
                </div>
              </div>

              <div className="p-6 md:p-7">
                <div className="flex items-start justify-between mb-3">
                  <span className="font-jetbrains text-[11px] tracking-[0.2em] text-[var(--gold)]">
                    {String(i + 1).padStart(2, "0")} / {String(s.items.length).padStart(2, "0")}
                  </span>
                  <ArrowUpRight className="w-5 h-5 text-[var(--text-secondary)] group-hover:text-[var(--gold)] group-hover:rotate-45 transition-all duration-300" />
                </div>
                <h3 className="font-cormorant text-2xl md:text-3xl text-[var(--text-primary)] mb-2">{item.t}</h3>
                <p className="font-outfit font-light text-sm text-[var(--text-secondary)] leading-relaxed">
                  {item.d}
                </p>
              </div>
            </motion.div>
          ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};
